<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import FormSection from "@/Components/FormSection.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import { useForm } from "@inertiajs/inertia-vue3";
import { computed } from "vue";

const props = defineProps({
  tenant: { type: Object, required: true },
  buttonVisibility: { type: Object, default: () => ({}) },
  buttonVisibilityFields: { type: Array, default: () => [] },
});

const groupedButtonVisibilityFields = computed(() => {
  return props.buttonVisibilityFields.reduce((groups, field) => {
    const groupName = field.group || "Buttons";

    if (!groups[groupName]) {
      groups[groupName] = [];
    }

    groups[groupName].push(field);

    return groups;
  }, {});
});

const form = useForm({
  _method: "PUT",
  button_visibility: { ...props.buttonVisibility },
});

const saveForm = () => {
  form.post(route("admin.tenant-manager.buttons.update", { tenant: props.tenant.id }), {
    preserveScroll: true,
    onSuccess: () => {
      window.location.href = route("admin.tenant-manager.index");
    },
  });
};
</script>

<template>
  <AppLayout title="Tenant Buttons">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Button Visibility: {{ props.tenant.tenantName }}
      </h2>
    </template>

    <div class="max-w-4xl mx-auto py-10 sm:px-6 lg:px-8">
      <FormSection gridClasses="grid-cols-1" :noGrid="true">
        <template #title>
          <a :href="route('admin.tenant-manager.edit', { tenant: props.tenant.id })">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>

        <template #form>
          <div class="flex flex-col gap-4">
            <div
              v-for="(fields, groupName) in groupedButtonVisibilityFields"
              :key="groupName"
              class="rounded border border-gray-200 overflow-hidden"
            >
              <div class="bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
                {{ groupName }}
              </div>
              <div class="divide-y divide-gray-200">
                <div
                  v-for="field in fields"
                  :key="field.key"
                  class="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <div class="min-w-0">
                    <div class="text-sm font-medium text-gray-900">
                      {{ field.label }}
                    </div>
                    <div class="text-xs text-gray-500 mt-1">
                      {{ field.location }}
                    </div>
                    <div v-if="field.description" class="text-xs text-gray-400 mt-1">
                      {{ field.description }}
                    </div>
                    <div class="text-[11px] text-gray-400 mt-2 font-mono">
                      {{ field.key }}
                    </div>
                  </div>

                  <label class="inline-flex items-center cursor-pointer shrink-0">
                    <input
                      v-model="form.button_visibility[field.key]"
                      type="checkbox"
                      class="sr-only peer"
                    />
                    <div
                      class="relative w-11 h-6 bg-gray-300 rounded-full transition-colors
                      peer-focus:outline-none peer-focus:ring-1 peer-focus:ring-green-400
                      peer-checked:bg-green-500
                      after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full
                      after:transition-all after:left-0.5 after:top-0.5
                      peer-checked:after:translate-x-5"
                    ></div>
                    <span class="ml-3 text-sm text-gray-700">
                      {{ form.button_visibility[field.key] ? "Visible" : "Hidden" }}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div v-if="form.errors.button_visibility" class="text-sm text-red-600">
              {{ form.errors.button_visibility }}
            </div>
          </div>
        </template>

        <template #actions>
          <PrimaryButton @click.prevent="saveForm">
            Save
          </PrimaryButton>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
